import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Plus, Trash2, Save, AlertCircle } from 'lucide-react';

const CURRENCY_OPTIONS = ['EUR', 'USD', 'XAF', 'GBP', 'CAD'];

const RoutePricingGrid = ({ token }) => {
  const { t } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [origins, setOrigins] = useState([]);
  const [destinations, setDestinations] = useState([]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/company/settings');
      const data = res.data;
      setSettings(data);
      setRoutes(data.financial?.route_pricing || []);

      const hqs = (data.general?.headquarters || []).map(h => ({
        name: h.name || h.country_name || '',
        code: h.code || h.country_code || '',
      }));
      const dels = (data.general?.delivery_countries || []).map(d => ({
        name: d.name || d.country_name || '',
        code: d.code || d.country_code || '',
      }));
      setOrigins(hqs);
      setDestinations(dels);
    } catch {
      setError(t('cannotLoadSettings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const addRoute = () => {
    setRoutes([...routes, {
      origin_country: origins[0]?.code || '',
      destination_country: destinations[0]?.code || '',
      rate_per_kg: 0,
      currency: 'EUR',
      is_active: true,
    }]);
  };

  const updateRoute = (index, field, value) => {
    const updated = [...routes];
    updated[index] = { ...updated[index], [field]: value };
    setRoutes(updated);
  };

  const removeRoute = (index) => {
    setRoutes(routes.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        financial: {
          ...settings.financial,
          route_pricing: routes.map(r => ({
            ...r,
            rate_per_kg: parseFloat(r.rate_per_kg) || 0,
          })),
        },
      };
      await api.put('/api/company/settings', payload);
      setSuccess(t('pricingSaved'));
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError(t('saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-4">{t('loading')}</div>;
  }

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t('routePricing')}</CardTitle>
          <Button size="sm" variant="outline" onClick={addRoute}>
            <Plus size={14} className="mr-1" />
            {t('add')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="flex items-center gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 mb-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
            {success}
          </div>
        )}

        {routes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {t('noRoutesConfigured')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('origin')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('destination')}</th>
                  <th className="text-right px-3 py-2 font-medium text-muted-foreground">{t('pricePerKg')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('currency')}</th>
                  <th className="text-center px-3 py-2 font-medium text-muted-foreground">{t('active')}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {routes.map((route, idx) => (
                  <tr key={idx} className="hover:bg-muted">
                    <td className="px-3 py-2">
                      <select
                        value={route.origin_country}
                        onChange={(e) => updateRoute(idx, 'origin_country', e.target.value)}
                        className="w-full border border-border rounded px-2 py-1 text-sm"
                      >
                        <option value="">--</option>
                        {origins.map((o, i) => (
                          <option key={i} value={o.code}>{o.name} ({o.code})</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={route.destination_country}
                        onChange={(e) => updateRoute(idx, 'destination_country', e.target.value)}
                        className="w-full border border-border rounded px-2 py-1 text-sm"
                      >
                        <option value="">--</option>
                        {destinations.map((d, i) => (
                          <option key={i} value={d.code}>{d.name} ({d.code})</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={route.rate_per_kg}
                        onChange={(e) => updateRoute(idx, 'rate_per_kg', e.target.value)}
                        className="text-sm text-right w-24"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={route.currency}
                        onChange={(e) => updateRoute(idx, 'currency', e.target.value)}
                        className="border border-border rounded px-2 py-1 text-sm"
                      >
                        {CURRENCY_OPTIONS.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={route.is_active}
                        onChange={(e) => updateRoute(idx, 'is_active', e.target.checked)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => removeRoute(idx)}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {routes.length > 0 && (
          <div className="flex justify-end mt-4">
            <Button onClick={handleSave} disabled={saving} size="sm">
              <Save size={14} className="mr-1" />
              {saving ? t('savingInProgress') : t('saveBtn')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RoutePricingGrid;
